import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, Trash2, Activity, MonitorSmartphone, Keyboard, MousePointer2 } from 'lucide-react';
import Modal from '@/components/Modal';
import { fileUrl } from '@/services';
import { fmtDateTime } from '@/utils/format';

/**
 * Screenshot lightbox shared by the tracker day view and the screenshot gallery.
 *
 * Usage:
 *   const [lightbox, setLightbox] = useState(null); // { items: [...], index: 0 }
 *   <ScreenshotLightbox state={lightbox} onClose={() => setLightbox(null)}
 *     onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
 *     onDelete={(shot) => ...} />
 */
export default function ScreenshotLightbox({ state, onClose, onIndexChange, onDelete }) {
  const items = state?.items || [];
  const index = state?.index ?? 0;
  const shot = items[index];

  const prev = () => onIndexChange?.(Math.max(0, index - 1));
  const next = () => onIndexChange?.(Math.min(items.length - 1, index + 1));

  useEffect(() => {
    if (!shot) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!shot) return null;

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={`Screenshot — ${fmtDateTime(shot.capturedAt)}`}
      footer={
        <>
          <div className="mr-auto text-sm text-slate-500">
            {index + 1} of {items.length}
          </div>
          <button className="btn-secondary" onClick={prev} disabled={index <= 0}>
            <ChevronLeft size={14} /> Prev
          </button>
          <button className="btn-secondary" onClick={next} disabled={index >= items.length - 1}>
            Next <ChevronRight size={14} />
          </button>
          {onDelete && (
            <button className="btn-danger" onClick={() => onDelete(shot)}>
              <Trash2 size={14} /> Delete
            </button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800">
          <img
            src={fileUrl(shot.file)}
            alt={shot.activeTitle || 'Screenshot'}
            className="w-full max-h-[60vh] object-contain"
          />
        </div>

        {shot.user && (
          <div className="text-sm">
            <span className="font-medium">{shot.user?.name}</span>
            {shot.user?.employeeId && <span className="text-slate-500"> · {shot.user.employeeId}</span>}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Meta icon={Activity} label="Activity" value={`${Math.round(shot.activityPercent || 0)}%`} />
          <Meta
            icon={MonitorSmartphone}
            label="Screen"
            value={
              shot.screenCount > 1
                ? `${(shot.screenIndex ?? 0) + 1} of ${shot.screenCount}`
                : `${shot.width || '?'}×${shot.height || '?'}`
            }
          />
          <Meta icon={Keyboard} label="Key presses" value={shot.keyCount ?? 0} />
          <Meta icon={MousePointer2} label="Mouse events" value={shot.mouseCount ?? 0} />
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Active application</div>
          <div className="font-medium">{shot.activeApp || 'Unknown'}</div>
          {shot.activeTitle && (
            <div className="text-sm text-slate-500 break-words">{shot.activeTitle}</div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 px-3 py-2">
      <Icon size={16} className="text-slate-400 shrink-0" />
      <span className="text-slate-500">{label}</span>
      <span className="ml-auto font-semibold">{value}</span>
    </div>
  );
}
